import { compact, defaultsDeep, get, map } from 'lodash';

import {
  RlaDefinition,
  Role,
  RoleType,
  RouteLinkActionConfig,
} from '@lockton-apps/shared/interfaces';
import { RouteLinkAction } from '@lockton-apps/shared/ui/lui';
import { NavActions } from '@lockton-apps/shared/action/misc';
import { isExperimental } from '@lockton-apps/shared/util/helpers';
import { InjectionToken } from '@angular/core';

export const userMenuMap = new Map<
  RoleType | 'PrimaryContact',
  RouteLinkAction[]
>([
  [
    Role.Administrator,
    [
      {
        id: 'my-account',
        type: 'route',
        label: $localize`My account`,
        route: 'my-account',
      },
      {
        id: 'users',
        type: 'route',
        label: $localize`Users`,
        route: 'users',
      },
      {
        id: 'logout',
        type: 'action',
        label: $localize`Logout`,
        icon: 'avatar',
        cb: () => NavActions.userMenuLogout(),
      },
    ],
  ],
  [
    Role.LeadBroker,
    [
      { type: 'route', label: $localize`My account`, route: 'my-account' },
      {
        id: 'logout',
        type: 'action',
        label: $localize`Logout`,
        cb: () => NavActions.userMenuLogout(),
      },
    ],
  ],
  [
    Role.Broker,
    [
      { type: 'route', label: $localize`My account`, route: 'my-account' },
      {
        id: 'logout',
        type: 'action',
        label: $localize`Logout`,
        cb: () => NavActions.userMenuLogout(),
      },
    ],
  ],
  [
    'PrimaryContact',
    [
      {
        id: 'my-account',
        type: 'route',
        label: $localize`My account`,
        route: 'my-account',
      },
      {
        id: 'manage-contacts',
        type: 'route',
        label: $localize`Manage contacts`,
        route: 'contacts',
      },
      {
        id: 'logout',
        type: 'action',
        label: $localize`Logout`,
        icon: 'avatar',
        cb: () => NavActions.userMenuLogout(),
      },
    ],
  ],
  [
    Role.Contact,
    [
      {
        id: 'my-account',
        type: 'route',
        label: $localize`My account`,
        route: 'my-account',
      },
      {
        id: 'logout',
        type: 'action',
        label: $localize`Logout`,
        icon: 'avatar',
        cb: () => NavActions.userMenuLogout(),
      },
    ],
  ],
]);

export const defaultPrimaryNavBrokerLinks: RouteLinkActionConfig[] = compact([
  { id: 'home' },
  { id: 'clients' },
  { id: 'brokers' },
  // EXPERIMENTAL:STORY:LCP-511
  isExperimental() ? { id: 'faqs' } : undefined,
  isExperimental() ? { id: 'newsInternal' } : undefined,
]);

export const defaultPrimaryNavContactLinks: RouteLinkActionConfig[] = compact([
  { id: 'home' },
  { id: 'my-policies' },
  // EXPERIMENTAL:STORY:LCP-511
  isExperimental() ? { id: 'faqs' } : undefined,
  isExperimental() ? { id: 'newsInternal' } : undefined,
]);

export const defaultFooterLinks: RouteLinkActionConfig[] = compact([
  { id: 'privacy' },
  { id: 'cookies' },
  { id: 'terms' },
  // EXPERIMENTAL:STORY:LCP-511
  isExperimental() ? { id: 'faqs' } : undefined,
  { id: 'mainSite' },
]);

export function createRlaLinks(
  links: RouteLinkActionConfig[],
  definition: RlaDefinition
) {
  return map(links, (link): RouteLinkAction => {
    if (link.children) {
      return {
        ...link,
        children: createRlaLinks(link.children, definition),
      } as RouteLinkAction;
    }

    return defaultsDeep(
      link,
      get(definition, link.id as string)
    ) as RouteLinkAction;
  });
}

export const NAV_DEFINITION_DEFAULT = {
  home: {
    id: 'home',
    label: $localize`Home`,
    type: 'route',
    route: ['/'],
  },
  clients: {
    id: 'clients',
    label: $localize`Clients`,
    type: 'route',
    route: ['/clients'],
  },
  brokers: {
    id: 'brokers',
    label: $localize`Brokers`,
    type: 'route',
    route: ['/brokers'],
  },
  newsInternal: {
    id: 'newsInternal',
    label: $localize`News & events`,
    type: 'route',
    route: ['/news'],
  },
  newsLsos: {
    id: 'newsLsos',
    label: $localize`News & events`,
    type: 'link',
    href: 'https://www.locktonlaw.scot/news/',
    target: '_blank',
  },
  'my-policies': {
    id: 'my-policies',
    label: $localize`My policies`,
    type: 'route',
    route: ['/policies'],
  },
  'my-lockton-team': {
    id: 'my-lockton-team',
    label: $localize`My Lockton team`,
    type: 'route',
    route: ['/my-lockton-team'],
  },
  privacy: {
    id: 'privacy',
    type: 'link',
    label: $localize`Privacy notice`,
    href: 'https://global.lockton.com/privacy-notice',
    target: '_blank',
  },
  cookies: {
    id: 'cookies',
    type: 'route',
    label: $localize`Cookies notice`,
    route: ['/cookies'],
  },
  terms: {
    id: 'terms',
    type: 'route',
    label: $localize`Terms of use`,
    route: ['/terms-of-use'],
    target: '_blank',
  },
  faqs: {
    id: 'faqs',
    type: 'route',
    label: $localize`FAQs`,
    route: ['/faqs'],
  },
  mainSite: {
    id: 'mainSite',
    type: 'link',
    label: $localize`Lockton global`,
    href: 'https://global.lockton.com/',
    target: '_blank',
  },
};

export const NAV_DEFINITION_LATAM = {
  home: {
    id: 'home',
    label: $localize`Home`,
    type: 'route',
    route: ['/'],
  },
  clients: {
    id: 'clients',
    label: $localize`Clients`,
    type: 'route',
    route: ['/clients'],
  },
  brokers: {
    id: 'brokers',
    label: $localize`Brokers`,
    type: 'route',
    route: ['/brokers'],
  },
  newsInternal: {
    id: 'newsInternal',
    label: $localize`News & events`,
    type: 'route',
    route: ['/news'],
  },
  'my-policies': {
    id: 'my-policies',
    label: $localize`My policies`,
    type: 'route',
    route: ['/policies'],
  },
  'my-lockton-team': {
    id: 'my-lockton-team',
    label: $localize`My Lockton team`,
    type: 'route',
    route: ['/my-lockton-team'],
  },
  privacy: {
    id: 'privacy',
    type: 'link',
    label: $localize`Privacy notice`,
    href: 'https://global.lockton.com/privacy-notice',
    target: '_blank',
  },
  cookies: {
    id: 'cookies',
    type: 'route',
    label: $localize`Cookies notice`,
    route: ['/cookies'],
  },
  terms: {
    id: 'terms',
    type: 'link',
    label: $localize`Terms of use`,
    href: 'https://global.lockton.com/latam/es/terminos-y-condiciones',
    target: '_blank',
  },
  mainSite: {
    id: 'mainSite',
    type: 'link',
    label: $localize`Lockton global`,
    href: 'https://global.lockton.com/',
    target: '_blank',
  },
};

export const NAV_DEFINITION = new InjectionToken<RlaDefinition>(
  'Definition of nav items'
);
